import React from 'react';
import { Icon } from '@plone/volto/components';
import { blockHasValue, buildStyleClassNamesFromData } from '@plone/volto/helpers';
import { Button } from 'semantic-ui-react';
import includes from 'lodash/includes';
import isBoolean from 'lodash/isBoolean';
import { defineMessages, injectIntl } from 'react-intl';
import cx from 'classnames';
import config from '@plone/volto/registry';
import { BlockChooserButton } from '@plone/volto/components';
import { v4 as uuid } from 'uuid';

import dragSVG from '@plone/volto/icons/drag.svg';
import trashSVG from '@plone/volto/icons/delete.svg';
import hideSVG from '@plone/volto/icons/hide.svg';
import showSVG from '@plone/volto/icons/show.svg';
import lockOffSVG from '@plone/volto/icons/lock-off.svg';
import restoreSVG from '@plone/volto/icons/restore.svg';

const messages = defineMessages({
  delete: {
    id: 'delete',
    defaultMessage: 'delete',
  },
  inheritedBadge: {
    id: 'slot_inherited_badge',
    defaultMessage: 'Herdado',
  },
  inheritedBadgeTitle: {
    id: 'slot_inherited_badge_title',
    defaultMessage: 'Este componente vem de uma página superior. Você pode ocultá-lo ou personalizá-lo apenas nesta página.',
  },
  hiddenBadge: {
    id: 'slot_hidden_badge',
    defaultMessage: 'Oculto nesta página',
  },
  notShownBadge: {
    id: 'slot_not_shown_badge',
    defaultMessage: 'Não exibido',
  },
  notShownNote: {
    id: 'slot_not_shown_note',
    defaultMessage: 'Não aparece nesta página: vale o componente igual de uma página mais próxima.',
  },
  hideOnThisPage: {
    id: 'slot_hide_on_this_page',
    defaultMessage: 'Ocultar nesta página',
  },
  showAgain: {
    id: 'slot_show_again',
    defaultMessage: 'Mostrar novamente',
  },
  unlock: {
    id: 'unlock',
    defaultMessage: 'Personalizar nesta página',
  },
  restoreHidden: {
    id: 'slot_restore_hidden',
    defaultMessage: 'Mostrar novamente',
  },
  restoreCustomized: {
    id: 'slot_restore_customized',
    defaultMessage: 'Restaurar versão herdada',
  },
});

export const customizeBlock = (block, data, formData) => {
  const newId = uuid();
  const newFormData = {
    blocks_layout: {
      ...formData.blocks_layout,
      items: [...formData.blocks_layout.items],
    },
    blocks: {
      ...formData.blocks,
      [newId]: {
        ...formData.blocks[block],
        's:isVariantOf': block,
        _v_original: { ...data },
      },
    },
  };

  delete newFormData.blocks[block];

  const ix = formData.blocks_layout.items.indexOf(block);
  newFormData.blocks_layout.items[ix] = newId;
  return [newFormData, newId];
};

export const restoreBlock = (block, data, formData) => {
  // replace a block with its older variant
  // console.log('restore', { block, data, formData });
  const oldId = data['s:isVariantOf'];

  const newFormData = {
    blocks_layout: {
      ...formData.blocks_layout,
      items: [...formData.blocks_layout.items],
    },
    blocks: {
      ...formData.blocks,
      [oldId]: {
        ...data._v_original, //  || formData.blocks[oldId]
      },
    },
  };

  delete newFormData.blocks[block];

  const ix = formData.blocks_layout.items.indexOf(block);
  newFormData.blocks_layout.items[ix] = oldId;
  return [newFormData, oldId];
};

/**
 * Ocultar/mostrar TODOS os itens herdados de uma vez — o atalho para o botão
 * de olho por bloco (`onHiddenBlock`). Percorre a lista aplicando exatamente
 * as mesmas transformações já usadas item a item (`customizeBlock` para
 * ocultar, `restoreBlock` para reexibir), acumulando sobre um `formData` de
 * trabalho, para não divergir da lógica testada.
 *
 * - `hidden: true`  → cada bloco HERDADO e ainda visível vira uma variante
 *   local marcada com `v:hidden` (é o que faz o backend não renderizá-lo).
 * - `hidden: false` → cada variante oculta por este caminho volta a ser o
 *   bloco herdado (readOnly + `_v_inherit`).
 *
 * Blocos locais (não herdados) e placeholders vazios são deixados como estão:
 * o herdado se oculta, o próprio da página se apaga (botão de lixeira).
 */
export const setAllInheritedHidden = (formData, hidden) => {
  let working = formData;
  const items = [...(formData?.blocks_layout?.items || [])];

  items.forEach((id) => {
    const data = working.blocks?.[id];
    if (!data) return;

    const isInherited = !!data._v_inherit;
    const isVariant = !!data['s:isVariantOf'];
    const isHidden = !!data['v:hidden'];

    if (hidden && isInherited && !isHidden) {
      const [next, newId] = customizeBlock(id, data, working);
      next.blocks[newId] = { ...next.blocks[newId], 'v:hidden': true };
      delete next.blocks[newId]._v_inherit;
      working = { ...working, ...next };
    } else if (!hidden && isVariant && isHidden) {
      const [next, newId] = restoreBlock(id, data, working);
      next.blocks[newId] = {
        ...next.blocks[newId],
        _v_inherit: true,
        readOnly: true,
      };
      working = { ...working, ...next };
    }
  });

  return working;
};

/**
 * Existe pelo menos um bloco herdado ainda VISÍVEL? (decide se a ação global
 * disponível é "ocultar todos" ou "mostrar todos").
 */
export const hasVisibleInheritedBlocks = (formData) => Object.values(formData?.blocks || {}).some((b) => b && b._v_inherit && !b['v:hidden']);

/**
 * Existe pelo menos uma variante que foi ocultada por este caminho? (para
 * saber se há o que reexibir).
 */
export const hasHiddenInheritedBlocks = (formData) => Object.values(formData?.blocks || {}).some((b) => b && b['s:isVariantOf'] && b['v:hidden']);

const SlotEditBlockWrapper = (props) => {
  const hideHandler = (data) => {
    return !!data.fixed || (!config.experimental.addBlockButton.enabled && !(blockHasValue(data) && props.blockProps.editable));
  };

  const { intl, blockProps, draginfo, children } = props;
  const { allowedBlocks, block, blocksConfig, selected, type, onChangeField, onChangeBlock, onDeleteBlock, onInsertBlock, onSelectBlock, onMutateBlock, data, editable, properties, showBlockChooser } = blockProps;

  const visible = selected && !hideHandler(data);
  const inherited = data._v_inherit ? 'slot-inherited' : null;
  const blockIsHidden = !!data['v:hidden'];
  const blockIsVariant = !!data['s:isVariantOf'];
  // O site pode ter componentes que só aparecem uma vez na lateral (ex.: o
  // menu de contexto); o que perde não ganha prévia, só uma nota curta.
  const notShown = !blockIsHidden && !!config.settings.slotsBlockNotShown?.(block, properties);

  const required = isBoolean(data.required) ? data.required : includes(config.blocks.requiredBlocks, type);

  const styles = buildStyleClassNamesFromData(data.styles);

  // Função para exibir ou esconder o bloco
  const onHiddenBlock = () => {
    const op = blockIsHidden ? restoreBlock : customizeBlock;
    const [newFormData, blockId] = op(block, data, properties);
    newFormData.blocks[blockId] = {
      ...newFormData.blocks[blockId],
      'v:hidden': !blockIsHidden,
    };

    if (!blockIsHidden) {
      delete newFormData.blocks[blockId]._v_inherit;
    }
    onChangeField('blocks', newFormData['blocks']);
    onChangeField('blocks_layout', newFormData['blocks_layout']);
    onSelectBlock(blockId);
  };

  const onLockUnlockBlock = () => {
    const [newFormData, blockId] = customizeBlock(block, data, properties);
    delete newFormData.blocks[blockId]._v_inherit;
    delete newFormData.blocks[blockId].readOnly;

    onChangeField('blocks', newFormData['blocks']);
    onChangeField('blocks_layout', newFormData['blocks_layout']);
    onSelectBlock(blockId);
  };

  const onRestoreBlock = () => {
    const [newFormData, blockId] = restoreBlock(block, data, properties);
    newFormData.blocks[blockId]._v_inherit = true;
    newFormData.blocks[blockId].readOnly = true;

    onChangeField('blocks', newFormData['blocks']);
    onChangeField('blocks_layout', newFormData['blocks_layout']);
    onSelectBlock(blockId);
  };

  return (
    <div
      ref={draginfo.innerRef}
      {...draginfo.draggableProps}
      // Right now, we can have the alignment information in the styles property or in the
      // block data root, we inject the classname here for having control over the whole
      // Block Edit wrapper
      className={cx('slot-block', `slot-editor-${data['@type']}`, styles, {
        [data.align]: data.align,
        'slot-inherited': inherited,
        'slot-hidden': blockIsHidden,
        'slot-not-shown': notShown,
      })}
    >
      <div style={{ position: 'relative' }}>
        <div
          style={{
            visibility: visible ? 'visible' : 'hidden',
            display: 'inline-block',
          }}
          {...draginfo.dragHandleProps}
          className="drag handle wrapper"
        >
          <Icon name={dragSVG} size="18px" />
        </div>

        <div className={`ui drag block inner ${type}`}>
          {/* RF01(a)/(b): rótulo visível (não só um ::before em CSS) explicando
              a origem/estado do componente, para o editor entender de onde ele
              vem e o que pode fazer com ele — ver `SlotSchema`/`block_parent`
              sobre a barra lateral herdada como base de "com/sem sidebar". */}
          {inherited && (
            <span className="slot-badge slot-badge--inherited" title={intl.formatMessage(messages.inheritedBadgeTitle)}>
              {intl.formatMessage(messages.inheritedBadge)}
            </span>
          )}
          {blockIsHidden && <span className="slot-badge slot-badge--hidden">{intl.formatMessage(messages.hiddenBadge)}</span>}
          {notShown && <span className="slot-badge slot-badge--not-shown">{intl.formatMessage(messages.notShownBadge)}</span>}

          {notShown ? <p className="slot-not-shown-note">{intl.formatMessage(messages.notShownNote)}</p> : children}
          {/* Botão de delete para os blocos normais*/}
          {selected && !required && editable && !inherited && !blockIsHidden && !blockIsVariant && (
            <Button icon basic onClick={() => onDeleteBlock(block, true)} className="delete-button" aria-label={intl.formatMessage(messages.delete)} title={intl.formatMessage(messages.delete)}>
              <Icon name={trashSVG} size="18px" />
            </Button>
          )}

          <div className="slot-buttons-actions">
            {/* Botão de ocultar/mostrar o bloco herdado — mesmo botão alterna
                os dois estados, então ícone e rótulo refletem a AÇÃO
                disponível (não o estado atual). */}
            {selected && inherited && (
              <Button icon basic onClick={() => onHiddenBlock()} className="hidden-button" aria-label={intl.formatMessage(blockIsHidden ? messages.showAgain : messages.hideOnThisPage)} title={intl.formatMessage(blockIsHidden ? messages.showAgain : messages.hideOnThisPage)}>
                <Icon name={blockIsHidden ? showSVG : hideSVG} size="18px" />
              </Button>
            )}

            {/* Botão para personalizar o bloco herdado (desvincula da herança,
                permitindo edição local a partir daqui). */}
            {selected && !blockIsHidden && inherited && (
              <Button icon basic onClick={() => onLockUnlockBlock()} className="unlock-button" aria-label={intl.formatMessage(messages.unlock)} title={intl.formatMessage(messages.unlock)}>
                <Icon name={lockOffSVG} size="18px" />
              </Button>
            )}
          </div>

          {/* Botão para restaurar a versão herdada de um bloco personalizado
              (ou reexibir um bloco herdado que foi ocultado por este caminho). */}
          {selected && blockIsVariant && (
            <Button icon basic onClick={() => onRestoreBlock()} className="delete-button" aria-label={intl.formatMessage(blockIsHidden ? messages.restoreHidden : messages.restoreCustomized)} title={intl.formatMessage(blockIsHidden ? messages.restoreHidden : messages.restoreCustomized)}>
              <Icon name={blockIsHidden ? showSVG : restoreSVG} size="18px" />
            </Button>
          )}

          {config.experimental.addBlockButton.enabled && showBlockChooser && (
            <BlockChooserButton
              data={data}
              block={block}
              onInsertBlock={(id, value) => {
                if (blockHasValue(data)) {
                  onSelectBlock(onInsertBlock(id, value));
                } else {
                  onChangeBlock(id, value);
                }
              }}
              onMutateBlock={onMutateBlock}
              allowedBlocks={allowedBlocks}
              blocksConfig={blocksConfig}
              size="24px"
              properties={properties}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default injectIntl(SlotEditBlockWrapper);
